/**
 * The FOCUS PANE registry — which surfaces can open beside the conversation.
 *
 * The pattern (operator direction 2026-07-21): a working surface takes the
 * right-hand pane while the chat keeps the left, and the other columns collapse
 * out of the way. The lawyer then drives the surface EITHER by clicking it or
 * by prompting the concierge, watching the result change in place. Tasklist was
 * the first; the browser and contacts want the same treatment.
 *
 * Adding a surface is one entry here plus its id in `FocusPaneId` — no layout
 * rewiring, no new shell component. That is the whole point of the registry.
 *
 * `gate` is optional: a surface the runtime can switch off (as the tasklist can
 * via `LEX_TASKLIST_ENABLE`) returns `false` and the pane refuses to open.
 */
import { useEffect, useState, type ReactNode } from "react";

import type { FocusPaneId } from "@/stores/useUIStore";
import { TasklistView } from "@/components/tasklist/TasklistView";
import { ScheduleView } from "@/components/schedule/ScheduleView";
import { AgentBrowserLayout } from "@/layouts/AgentBrowserLayout";
import ContactsLayout from "@/layouts/ContactsLayout";
import { AgentSurface, isAgentSection, type AgentSection } from "@/components/agent/AgentSurface";
import { useUIStore } from "@/stores/useUIStore";

export interface FocusPaneSurface {
  /** i18n key for the pane's title bar. */
  titleKey: string;
  /**
   * `narrow` is the measured verdict from the HOST (pane width, not viewport):
   * a surface with its own master-detail split needs it, others may ignore it.
   */
  render: (opts: { narrow: boolean }) => ReactNode;
  /**
   * Some surfaces read the URL (`useParams`/`useSearchParams`). Hosted in a
   * pane they see whatever route is currently active, so they render their
   * LIST state rather than a URL-selected item. Flagged per-surface so the
   * behaviour is a recorded decision instead of a surprise.
   */
  routeCoupled?: boolean;
  /**
   * Suppress the host's title bar — the surface provides its OWN heading (e.g.
   * the Agent pane's identity hero), so the host's "Agente" bar is redundant.
   * The host then renders only a floating close, reclaiming the bar's vertical
   * space so the surface starts at the top.
   */
  bareHeader?: boolean;
  /**
   * The surface renders its OWN close control (the Agent hero relocated it into
   * its right cluster — §3.1), so the host renders none. Avoids the two-close
   * collision the redesign's W-1 finding flagged. Only meaningful with
   * `bareHeader`; the surface is handed `onClose` via its own wiring.
   */
  ownsClose?: boolean;
}

export const FOCUS_PANE_SURFACES: Record<FocusPaneId, FocusPaneSurface> = {
  tasks: {
    titleKey: "tasklist.sectionTitle",
    // Its own list/detail split keys off the PANE width, not the viewport.
    // Scoped when the case header's "N pendências" backlink opened it, global
    // otherwise — so the signal lands on the pendências it was counting.
    render: ({ narrow }) => <ScopedTasklistView compact={narrow} />,
  },
  schedule: {
    // Agendamentos — the CLOCK sibling of Pendências (agendamentos plan §1).
    // Single-column list; no master-detail split, so `narrow` is ignored.
    titleKey: "nav.schedule",
    render: () => <ScheduleView />,
  },
  agentBrowser: {
    titleKey: "nav.agentBrowser",
    // Self-contained flex column — no route coupling.
    render: () => <AgentBrowserLayout />,
  },
  contacts: {
    titleKey: "nav.contacts",
    // Reads `contactId` from the route; in the pane there is none, so it opens
    // on the directory list. Selecting a contact inside the pane still works
    // through its own internal state.
    routeCoupled: true,
    render: () => <ContactsLayout />,
  },
  agent: {
    titleKey: "nav.agent",
    // The agent's browser + channels (email / Telegram / WhatsApp) + settings.
    // This is the PRIMARY shape for the surface: monitoring what the agent has
    // been doing is work the lawyer does WHILE prompting the concierge about it,
    // so it belongs beside the conversation rather than on a page that replaces
    // it. Its own identity hero is the heading, so it drops the host title bar,
    // and the hero owns the close (relocated into its cluster — W-1).
    bareHeader: true,
    ownsClose: true,
    render: () => <AgentPaneSurface />,
  },
};

/**
 * Section state for the agent surface in the pane. A focus pane has no URL, so
 * the route's `?section=` cannot back it — local state does. Defaults to BROWSER
 * (operator direction 2026-07-25): the agent's live browser is now the first and
 * default tab of the Agent pane, so opening the pane from the rail lands on it.
 * A deep link (`/agent?section=…`) still overrides via the one-shot
 * `agentPaneInitial` handoff; a section-less `/agent` falls through to this same
 * default.
 */
function AgentPaneSurface() {
  // A deep link (`/agent?section=…`) cannot reach a pane, which has no URL — the
  // `/agent` entry stashes its intent in `agentPaneInitial` and we consume it
  // exactly once here. Read during the FIRST render (lazy useState initialiser)
  // rather than in an effect, so the requested tab is what paints — an effect
  // would flash EMAIL first.
  const agentPaneInitial = useUIStore((s) => s.agentPaneInitial);
  const setAgentPaneInitial = useUIStore((s) => s.setAgentPaneInitial);
  const closePane = useUIStore((s) => s.closeFocusPane);
  // Narrow OUTSIDE the initialiser so the type guard actually flows through.
  const requested = agentPaneInitial?.section ?? null;
  // §4.5 alias: `?section=settings` no longer names a TAB (S4 retired it) —
  // it means "open the gear dialog". Land on the pane default and let the
  // surface open the dialog, so an old deep link still reaches its content.
  const [section, setSection] = useState<AgentSection>(
    isAgentSection(requested) && requested !== "settings" ? requested : "browser",
  );
  const [initialConfigOpen] = useState(requested === "settings");

  // In-pane maximize (§3.2) — a session gesture, deliberately NOT persisted.
  // Kept HERE (the pane's rendering wrapper, which unmounts when the pane
  // closes) so a fresh open always starts un-maximized, exactly as specified,
  // WITHOUT adding state to FocusPaneHost.
  const [maximized, setMaximized] = useState(false);

  // Clear the one-shot deep link after mount so the next open (from the rail,
  // with no deep link) starts on the pane default instead of a stale tab.
  useEffect(() => {
    if (agentPaneInitial) setAgentPaneInitial(null);
    // Mount-only: consuming is a one-shot, and re-running would fight a user
    // who changes tab while the field is still set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc RESTORES while maximized (§3.2), rather than closing the pane. A
  // capture-phase listener runs before FocusPaneHost's window keydown → close;
  // `stopImmediatePropagation` keeps that close from also firing. When NOT
  // maximized this listener is absent, so Esc bubbles to the host and closes.
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMaximized(false);
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [maximized]);

  return (
    <AgentSurface
      section={section}
      onSelect={setSection}
      maximized={maximized}
      onToggleMaximize={() => setMaximized((m) => !m)}
      onClose={closePane}
      initialConfigOpen={initialConfigOpen}
    />
  );
}

/** Scoped to one case when the header's pendências backlink opened the pane,
 *  global otherwise.
 *
 *  It SUBSCRIBES rather than reading once on mount. An earlier version cleared
 *  the value in a mount effect, which StrictMode's double-invoke wiped before
 *  the real render could read it — the pane opened global and the backlink
 *  silently did nothing. The store now owns the lifetime: every ordinary
 *  `openFocusPane` clears the scope, and `openTasksPaneForCase` sets it. */
function ScopedTasklistView({ compact }: { compact: boolean }) {
  const caseId = useUIStore((s) => s.tasksPaneCaseId);
  return <TasklistView compact={compact} caseId={caseId ?? undefined} />;
}
